import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { documentAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  ChevronLeft, FileText, FileSignature, UserCheck, LogOut,
  Loader2, Copy, Download, CheckCircle, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const DocumentGeneratorPage = () => {
  const navigate = useNavigate();
  const { isSubscribed } = useAuth();
  const [activeTab, setActiveTab] = useState('reference_request');
  const [loading, setLoading] = useState(false);
  const [generatedDocument, setGeneratedDocument] = useState('');
  const [copied, setCopied] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    recipient_name: '',
    addressed_to: '',
    company_name: '',
    job_title: '',
    start_date: '',
    end_date: '',
    reason: '',
    additional_info: ''
  });

  const documentTypes = [
    {
      id: 'reference_request',
      name: 'Reference Request',
      description: 'Ask someone to be your reference',
      icon: <UserCheck className="w-5 h-5" />
    },
    {
      id: 'reference_letter',
      name: 'Reference Letter',
      description: 'Template for your referee to use',
      icon: <FileText className="w-5 h-5" />
    },
    {
      id: 'resignation',
      name: 'Resignation Letter',
      description: 'Professional resignation letter',
      icon: <LogOut className="w-5 h-5" />
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerate = async () => {
    if (!isSubscribed) {
      toast.error('Please subscribe to use this feature');
      navigate('/subscribe');
      return;
    }

    setLoading(true);
    setGeneratedDocument('');

    try {
      const response = await documentAPI.generate({
        document_type: activeTab,
        ...formData
      });
      setGeneratedDocument(response.data.content);
      toast.success('Document generated!');
    } catch (error) {
      toast.error('Failed to generate document');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedDocument);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/documents/download-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: generatedDocument,
          document_type: activeTab
        })
      });
      
      if (!response.ok) throw new Error('PDF generation failed');
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeTab.replace('_', '-')}-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('PDF Downloaded!');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const renderForm = () => {
    switch (activeTab) {
      case 'reference_request':
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-slate-700">Former Manager/Colleague Name *</Label>
              <Input
                value={formData.recipient_name}
                onChange={(e) => handleInputChange('recipient_name', e.target.value)}
                placeholder="e.g., Mr. John Smith"
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label className="text-slate-700">Company You Worked At *</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => handleInputChange('company_name', e.target.value)}
                placeholder="e.g., ABC Company"
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label className="text-slate-700">Your Job Title There</Label>
              <Input
                value={formData.job_title}
                onChange={(e) => handleInputChange('job_title', e.target.value)}
                placeholder="e.g., Sales Assistant"
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label className="text-slate-700">Additional Context (Optional)</Label>
              <Textarea
                value={formData.additional_info}
                onChange={(e) => handleInputChange('additional_info', e.target.value)}
                placeholder="Any specific achievements or projects you worked on together..."
                className="mt-1.5 min-h-[100px]"
              />
            </div>
          </div>
        );

      case 'reference_letter':
        return (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800">
                <strong>Tip:</strong> Share this template with your referee. They can customize it with specific examples of your work.
              </p>
            </div>
            <div>
              <Label className="text-slate-700">Addressed To (Recipient) *</Label>
              <Input
                value={formData.addressed_to}
                onChange={(e) => handleInputChange('addressed_to', e.target.value)}
                placeholder="e.g., To Whom It May Concern, Mrs. Nkosi"
                className="mt-1.5 h-11"
                data-testid="ref-letter-addressed-to"
              />
            </div>
            <div>
              <Label className="text-slate-700">Employee Name (Your Name) *</Label>
              <Input
                value={formData.recipient_name}
                onChange={(e) => handleInputChange('recipient_name', e.target.value)}
                placeholder="e.g., Thabo Molefe"
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label className="text-slate-700">Company Name *</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => handleInputChange('company_name', e.target.value)}
                placeholder="e.g., XYZ Corporation"
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label className="text-slate-700">Job Title *</Label>
              <Input
                value={formData.job_title}
                onChange={(e) => handleInputChange('job_title', e.target.value)}
                placeholder="e.g., Customer Service Representative"
                className="mt-1.5 h-11"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Start Date</Label>
                <Input
                  value={formData.start_date}
                  onChange={(e) => handleInputChange('start_date', e.target.value)}
                  placeholder="e.g., January 2020"
                  className="mt-1.5 h-11"
                />
              </div>
              <div>
                <Label className="text-slate-700">End Date</Label>
                <Input
                  value={formData.end_date}
                  onChange={(e) => handleInputChange('end_date', e.target.value)}
                  placeholder="e.g., December 2023"
                  className="mt-1.5 h-11"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-700">Key Achievements/Qualities</Label>
              <Textarea
                value={formData.additional_info}
                onChange={(e) => handleInputChange('additional_info', e.target.value)}
                placeholder="List your key achievements, skills, and positive qualities..."
                className="mt-1.5 min-h-[100px]"
              />
            </div>
          </div>
        );

      case 'resignation':
        return (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-amber-800">
                <strong>SA Labour Law:</strong> Standard notice period is 1 week for &lt;6 months employment, 2 weeks for 6-12 months, or 4 weeks for 1+ years.
              </p>
            </div>
            <div>
              <Label className="text-slate-700">Addressed To (Manager/HR) *</Label>
              <Input
                value={formData.addressed_to}
                onChange={(e) => handleInputChange('addressed_to', e.target.value)}
                placeholder="e.g., Mr. Smith, Mrs. Dlamini, HR Department"
                className="mt-1.5 h-11"
                data-testid="resignation-addressed-to"
              />
            </div>
            <div>
              <Label className="text-slate-700">Company Name *</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => handleInputChange('company_name', e.target.value)}
                placeholder="e.g., Current Company Ltd"
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label className="text-slate-700">Your Current Job Title *</Label>
              <Input
                value={formData.job_title}
                onChange={(e) => handleInputChange('job_title', e.target.value)}
                placeholder="e.g., Marketing Coordinator"
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label className="text-slate-700">Last Working Day *</Label>
              <Input
                type="date"
                value={formData.end_date}
                onChange={(e) => handleInputChange('end_date', e.target.value)}
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label className="text-slate-700">Reason for Leaving (Optional)</Label>
              <Input
                value={formData.reason}
                onChange={(e) => handleInputChange('reason', e.target.value)}
                placeholder="e.g., Personal reasons, New opportunity"
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label className="text-slate-700">Additional Notes (Optional)</Label>
              <Textarea
                value={formData.additional_info}
                onChange={(e) => handleInputChange('additional_info', e.target.value)}
                placeholder="Any handover notes or special circumstances..."
                className="mt-1.5 min-h-[80px]"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white px-4 lg:px-8 py-6">
        <div className="max-w-5xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-slate-300 hover:text-white hover:bg-slate-800 mb-4"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Dashboard
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center">
              <FileSignature className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold">Document Generator</h1>
              <p className="text-slate-400">Create professional letters with AI assistance</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-8 px-4 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Document Type Selection */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl border border-slate-200 p-4 sticky top-4">
                <h2 className="font-semibold text-slate-900 mb-4">Document Type</h2>
                <div className="space-y-2">
                  {documentTypes.map((doc) => (
                    <button
                      key={doc.id}
                      onClick={() => { setActiveTab(doc.id); setGeneratedDocument(''); }}
                      className={`w-full text-left p-4 rounded-xl transition-all ${
                        activeTab === doc.id
                          ? 'bg-emerald-50 border-2 border-emerald-500'
                          : 'bg-slate-50 border-2 border-transparent hover:border-slate-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          activeTab === doc.id ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'
                        }`}>
                          {doc.icon}
                        </div>
                        <div>
                          <p className={`font-medium ${activeTab === doc.id ? 'text-emerald-900' : 'text-slate-900'}`}>
                            {doc.name}
                          </p>
                          <p className="text-xs text-slate-500">{doc.description}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Form & Output */}
            <div className="lg:col-span-2 space-y-6">
              {/* Form */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">
                  {documentTypes.find(d => d.id === activeTab)?.name} Details
                </h2>
                
                {renderForm()}

                <Button
                  onClick={handleGenerate}
                  disabled={loading}
                  className="w-full mt-6 h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Generate Document
                    </>
                  )}
                </Button>
              </div>

              {/* Output */}
              {generatedDocument && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-slate-900">Generated Document</h2>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCopy}
                        className="gap-2"
                      >
                        {copied ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                        {copied ? 'Copied!' : 'Copy'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDownload}
                        className="gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Download PDF
                      </Button>
                    </div>
                  </div>
                  
                  <div className="bg-slate-50 rounded-xl p-6 font-mono text-sm whitespace-pre-wrap border border-slate-200">
                    {generatedDocument}
                  </div>

                  <p className="text-xs text-slate-500 mt-4 text-center">
                    Review and customize this document before sending. Always proofread for accuracy.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DocumentGeneratorPage;
